/**
 * « 📥 Commandes IRSIA » — les bons de commande scannés des crèches deviennent des
 * bons de livraison Enfact (26/10/2026).
 *
 * Chaque lundi, Philippe recopiait 8 commandes à la main dans Enfact. Ici le PDF est lu
 * par l'IA, chaque ligne reçoit le produit « IR - … » (correspondance déjà confirmée,
 * sinon par le nom), et RIEN n'est créé avant contrôle : un article manquant se retire,
 * une quantité se corrige, un produit se choisit. Les correspondances confirmées sont
 * retenues pour la semaine suivante.
 *
 * Article absent d'Enfact (02/11/2026) : « ➕ Créer le produit » le crée au prix HT de la
 * commande IRSIA (+ 6 %) au moment de créer le bon ; si Enfact le refuse, la ligne part
 * en texte libre au même prix — un produit manquant ne bloque plus le bon.
 *
 * Les bons créés s'importent ensuite dans la tournée par « Bons Enfact » ; les caisses
 * EPS se notent à la préparation (ligne « Vidanges » du bon).
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

interface ProduitEnfact { id: number; nom: string; prix: number; tva: number }
interface Groupe { id: number; nom: string }
interface LigneLue {
  code: string;
  description: string;
  unite: string;
  quantite: number;
  prix_ht: number;
  produit_id: number | null;
  ecart_prix: string | null;
  nouveau_propose?: { nom: string; groupe_id: number | null };
}
interface CommandeLue {
  numero: string;
  date_livraison: string | null;
  creche: string;
  commune: string;
  titre: string;
  total_ht: number | null;
  deja_cree: boolean;
  lignes: LigneLue[];
}
/** Produit à créer chez Enfact pour un article absent du catalogue. */
interface Nouveau { nom: string; prix_ht: string; groupe_id: number | null }
interface LigneEtat { produit_id: number | null; nouveau: Nouveau | null; quantite: string; retiree: boolean }
interface Etat { inclus: boolean; lignes: LigneEtat[]; resultat?: { bon?: string; erreur?: string; notes?: string[] } }

interface Props {
  open: boolean;
  onClose: () => void;
  /** Après création : ouvrir « Bons Enfact » pour les placer dans la tournée. */
  onOuvrirBons: () => void;
}

const euros = (n: number) => n.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const lireNombre = (s: string) => Number(String(s).replace(',', '.').trim());
/** Prix d'une ligne de bon : HT + 6 %, au centime (comme le serveur). */
const tvac = (ht: number) => Math.round(ht * 1.06 * 100) / 100;
const NOUVEAU = 'nouveau';
const jour = (d: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' }) : 'date non lue';

export default function CommandesIrsiaModal({ open, onClose, onOuvrirBons }: Props) {
  const [fichier, setFichier] = useState<File | null>(null);
  const [lecture, setLecture] = useState(false);
  const [produits, setProduits] = useState<ProduitEnfact[]>([]);
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [commandes, setCommandes] = useState<CommandeLue[]>([]);
  const [etats, setEtats] = useState<Record<string, Etat>>({});
  const [envoi, setEnvoi] = useState<string | null>(null);

  const parId = useMemo(() => new Map(produits.map((p) => [p.id, p])), [produits]);
  const produitsTries = useMemo(
    () => [...produits].sort((a, b) => (/^IR\b/i.test(b.nom) ? 1 : 0) - (/^IR\b/i.test(a.nom) ? 1 : 0) || a.nom.localeCompare(b.nom, 'fr')),
    [produits],
  );

  if (!open) return null;

  const fermer = () => {
    setFichier(null);
    setCommandes([]);
    setEtats({});
    onClose();
  };

  const lire = async () => {
    if (!fichier) return;
    setLecture(true);
    try {
      const fd = new FormData();
      fd.append('pdf', fichier);
      const r: any = await api.post('/orders/commandes-irsia/lire', fd);
      const cs: CommandeLue[] = r?.commandes ?? [];
      setProduits(r?.produits ?? []);
      setGroupes(r?.groupes ?? []);
      setCommandes(cs);
      setEtats(Object.fromEntries(cs.map((c) => [c.numero, {
        inclus: !c.deja_cree,
        lignes: c.lignes.map((l) => ({ produit_id: l.produit_id, nouveau: null, quantite: String(l.quantite).replace('.', ','), retiree: false })),
      }])));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Lecture du PDF impossible');
    } finally {
      setLecture(false);
    }
  };

  const maj = (numero: string, f: (e: Etat) => Etat) => setEtats((x) => ({ ...x, [numero]: f(x[numero]) }));
  const majLigne = (numero: string, i: number, f: (l: LigneEtat) => LigneEtat) =>
    maj(numero, (e) => ({ ...e, lignes: e.lignes.map((l, k) => (k === i ? f(l) : l)) }));

  /**
   * « Créer le produit » : proposé depuis la ligne lue, et appliqué d'un geste à toutes
   * les crèches qui commandent le même article sans produit (créé une seule fois).
   */
  const choisirNouveau = (l: LigneLue) => {
    const nouveau: Nouveau = {
      nom: l.nouveau_propose?.nom ?? `IR - ${l.description}`,
      prix_ht: Number.isFinite(l.prix_ht) && l.prix_ht > 0 ? String(l.prix_ht).replace('.', ',') : '',
      groupe_id: l.nouveau_propose?.groupe_id ?? null,
    };
    setEtats((x) => {
      const y = { ...x };
      for (const c of commandes) {
        const e = y[c.numero];
        if (!e || e.resultat?.bon) continue;
        const lignes = e.lignes.map((s, k) =>
          c.lignes[k].code === l.code && !s.produit_id && !s.nouveau ? { ...s, nouveau } : s,
        );
        y[c.numero] = { ...e, lignes };
      }
      return y;
    });
  };
  const nouveauValide = (n: Nouveau | null) => !!n && n.nom.trim().length >= 3 && lireNombre(n.prix_ht) > 0;

  /** Ce qui empêche de créer ce bon, ou null. */
  const probleme = (e: Etat): string | null => {
    const gardees = e.lignes.filter((l) => !l.retiree);
    if (!gardees.length) return 'Aucun article à livrer';
    if (gardees.some((l) => !l.produit_id && !l.nouveau)) return 'Choisissez le produit Enfact de chaque ligne, créez-le, ou retirez la ligne';
    if (gardees.some((l) => l.nouveau && !nouveauValide(l.nouveau))) return 'Nouveau produit : un nom et un prix HT sont nécessaires';
    if (gardees.some((l) => !(lireNombre(l.quantite) > 0))) return 'Une quantité est vide ou nulle';
    return null;
  };

  const total = (e: Etat) =>
    e.lignes.reduce((s, l) => {
      const p = l.produit_id ? parId.get(l.produit_id) : null;
      const prix = p ? p.prix : l.nouveau && nouveauValide(l.nouveau) ? tvac(lireNombre(l.nouveau.prix_ht)) : null;
      const q = lireNombre(l.quantite);
      return l.retiree || prix === null || !(q > 0) ? s : s + Math.round(prix * q * 100) / 100;
    }, 0);

  const creer = async (numeros: string[]) => {
    const demandes = numeros.map((n) => {
      const c = commandes.find((x) => x.numero === n)!;
      const e = etats[n];
      return {
        commande: { numero: c.numero, creche: c.creche, commune: c.commune, date_livraison: c.date_livraison },
        lignes: c.lignes
          .map((l, i) => ({ l, s: e.lignes[i] }))
          .filter(({ s }) => !s.retiree)
          .map(({ l, s }) => ({
            code: l.code,
            produit_id: s.produit_id,
            quantite: lireNombre(s.quantite),
            ...(!s.produit_id && s.nouveau
              ? { nouveau: { nom: s.nouveau.nom.trim(), prix_ht: lireNombre(s.nouveau.prix_ht), groupe_id: s.nouveau.groupe_id } }
              : {}),
          })),
      };
    });
    setEnvoi(numeros.length === 1 ? numeros[0] : 'tous');
    try {
      const r: any = await api.post('/orders/commandes-irsia/creer', { commandes: demandes });
      const res: Array<{ numero: string; bon?: string; erreur?: string; notes?: string[] }> = r?.resultats ?? [];
      setEtats((x) => {
        const y = { ...x };
        for (const o of res) if (y[o.numero]) y[o.numero] = { ...y[o.numero], resultat: { bon: o.bon, erreur: o.erreur, notes: o.notes } };
        return y;
      });
      const ok = res.filter((o) => o.bon).length;
      const ko = res.length - ok;
      if (ok) toast.success(`${ok} bon(s) créé(s) chez Enfact`);
      if (ko) toast.error(`${ko} bon(s) non créé(s) — voir le détail`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Création impossible');
    } finally {
      setEnvoi(null);
    }
  };

  const aCreer = commandes.filter((c) => {
    const e = etats[c.numero];
    return e?.inclus && !e.resultat?.bon && !probleme(e);
  });
  const crees = commandes.filter((c) => etats[c.numero]?.resultat?.bon).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-sky-800 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">📥 Commandes IRSIA → bons Enfact</h2>
            <p className="text-sky-100 text-sm">Rien n’est créé avant votre contrôle, commande par commande</p>
          </div>
          <button onClick={fermer} className="text-white/80 hover:text-white text-3xl leading-none" aria-label="Fermer">×</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {commandes.length === 0 && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm font-semibold text-gray-700">PDF des bons de commande (scan)</span>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
                  className="mt-1 block w-full text-sm"
                />
              </label>
              <button
                onClick={lire}
                disabled={!fichier || lecture}
                className="px-5 py-3 rounded-lg bg-sky-700 text-white font-semibold disabled:opacity-50"
              >
                {lecture ? 'Lecture du PDF… (jusqu’à une minute)' : 'Lire les commandes'}
              </button>
            </div>
          )}

          {commandes.map((c) => {
            const e = etats[c.numero];
            if (!e) return null;
            const souci = probleme(e);
            const fait = e.resultat?.bon;
            return (
              <section
                key={c.numero}
                className={`rounded-xl border-2 p-4 space-y-3 ${fait ? 'border-emerald-300 bg-emerald-50/40' : e.inclus ? 'border-sky-300' : 'border-gray-200 opacity-70'}`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={e.inclus}
                    disabled={!!fait}
                    onChange={(ev) => maj(c.numero, (x) => ({ ...x, inclus: ev.target.checked }))}
                    className="mt-1 w-5 h-5"
                  />
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900">{c.titre}</div>
                    <div className="text-sm text-gray-600">À livrer le {jour(c.date_livraison)}</div>
                    {c.deja_cree && !fait && (
                      <div className="text-sm text-amber-800 font-medium">Un bon Enfact existe déjà pour cette commande.</div>
                    )}
                    {fait && <div className="text-sm text-emerald-800 font-semibold">✅ Bon {fait} créé chez Enfact</div>}
                    {e.resultat?.erreur && <div className="text-sm text-red-700 font-medium">⚠️ {e.resultat.erreur}</div>}
                    {e.resultat?.notes?.map((n) => (
                      <div key={n} className="text-sm text-sky-900">ℹ️ {n}</div>
                    ))}
                  </div>
                </label>

                {e.inclus && !fait && (
                  <>
                    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                      {c.lignes.map((l, i) => {
                        const s = e.lignes[i];
                        const choisi = s.produit_id ? parId.get(s.produit_id) : null;
                        return (
                          <li key={i} className={`px-3 py-2 space-y-1.5 ${s.retiree ? 'bg-gray-50' : ''}`}>
                            <div className="flex items-center gap-3">
                              <span className={`flex-1 min-w-0 ${s.retiree ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                {l.description}
                                <span className="text-gray-500 text-sm"> — {String(l.quantite).replace('.', ',')} {l.unite.toLowerCase()} à {euros(l.prix_ht)} HT</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => majLigne(c.numero, i, (x) => ({ ...x, retiree: !x.retiree }))}
                                className="shrink-0 text-sm text-red-700 underline"
                              >
                                {s.retiree ? 'Remettre' : 'Retirer'}
                              </button>
                            </div>
                            {!s.retiree && (
                              <div className="flex flex-wrap items-center gap-2">
                                <select
                                  value={s.produit_id ?? (s.nouveau ? NOUVEAU : '')}
                                  onChange={(ev) => {
                                    const v = ev.target.value;
                                    if (v === NOUVEAU) {
                                      majLigne(c.numero, i, (x) => ({ ...x, produit_id: null }));
                                      choisirNouveau(l);
                                    } else {
                                      majLigne(c.numero, i, (x) => ({ ...x, produit_id: v ? Number(v) : null, nouveau: null }));
                                    }
                                  }}
                                  className={`min-w-0 flex-1 rounded-lg border-2 px-2 py-2 text-sm ${s.produit_id || s.nouveau ? 'border-gray-300' : 'border-red-400'}`}
                                >
                                  <option value="">— produit Enfact —</option>
                                  <option value={NOUVEAU}>➕ Nouveau produit (prix de la commande IRSIA)</option>
                                  {produitsTries.map((p) => (
                                    <option key={p.id} value={p.id}>{p.nom} · {euros(p.prix)}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={s.quantite}
                                  onChange={(ev) => majLigne(c.numero, i, (x) => ({ ...x, quantite: ev.target.value }))}
                                  className="w-24 rounded-lg border-2 border-gray-300 px-2 py-2 text-right tabular-nums"
                                  aria-label="Quantité"
                                />
                              </div>
                            )}
                            {!s.retiree && !s.produit_id && !s.nouveau && (
                              <button
                                type="button"
                                onClick={() => choisirNouveau(l)}
                                className="text-sm font-semibold text-sky-800 underline"
                              >
                                ➕ Créer le produit « {l.nouveau_propose?.nom ?? l.description} » à {euros(l.prix_ht)} HT
                              </button>
                            )}
                            {!s.retiree && !s.produit_id && s.nouveau && (
                              <div className="rounded-lg bg-sky-50 border border-sky-200 p-2 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="text"
                                    value={s.nouveau.nom}
                                    onChange={(ev) => majLigne(c.numero, i, (x) => ({ ...x, nouveau: x.nouveau && { ...x.nouveau, nom: ev.target.value } }))}
                                    className="min-w-0 flex-1 rounded-lg border-2 border-gray-300 px-2 py-2 text-sm"
                                    aria-label="Nom du nouveau produit"
                                  />
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={s.nouveau.prix_ht}
                                    onChange={(ev) => majLigne(c.numero, i, (x) => ({ ...x, nouveau: x.nouveau && { ...x.nouveau, prix_ht: ev.target.value } }))}
                                    className="w-24 rounded-lg border-2 border-gray-300 px-2 py-2 text-right tabular-nums"
                                    aria-label="Prix HT du nouveau produit"
                                  />
                                  {groupes.length > 0 && (
                                    <select
                                      value={s.nouveau.groupe_id ?? ''}
                                      onChange={(ev) => majLigne(c.numero, i, (x) => ({ ...x, nouveau: x.nouveau && { ...x.nouveau, groupe_id: ev.target.value ? Number(ev.target.value) : null } }))}
                                      className="rounded-lg border-2 border-gray-300 px-2 py-2 text-sm"
                                      aria-label="Groupe du nouveau produit"
                                    >
                                      <option value="">— groupe —</option>
                                      {groupes.map((g) => (
                                        <option key={g.id} value={g.id}>{g.nom}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                <p className="text-xs text-sky-900">
                                  Sera créé chez Enfact avec le bon
                                  {nouveauValide(s.nouveau) ? ` : ${euros(tvac(lireNombre(s.nouveau.prix_ht)))} TVAC (HT + 6 %)` : ''}.
                                  Si Enfact le refuse, la ligne part en texte libre au même prix.
                                </p>
                              </div>
                            )}
                            {!s.retiree && choisi && l.produit_id === s.produit_id && l.ecart_prix && (
                              <p className="text-xs text-amber-800">Prix différent : {l.ecart_prix}</p>
                            )}
                          </li>
                        );
                      })}
                    </ul>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="text-sm text-gray-700 tabular-nums">
                        Total du bon : <strong>{euros(total(e))}</strong> TVAC
                        {c.total_ht !== null && <span className="text-gray-500"> (commande IRSIA : {euros(c.total_ht)} HT)</span>}
                      </span>
                      <button
                        onClick={() => creer([c.numero])}
                        disabled={!!souci || envoi !== null}
                        className="px-4 py-2.5 rounded-lg bg-sky-700 text-white font-semibold disabled:opacity-50"
                      >
                        {envoi === c.numero ? 'Création…' : 'Créer ce bon'}
                      </button>
                    </div>
                    {souci && <p className="text-sm text-red-700">{souci}</p>}
                  </>
                )}
              </section>
            );
          })}
        </div>

        {commandes.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
            <span className="text-sm text-gray-600">
              {crees > 0
                ? `${crees} bon(s) créé(s). Placez-les dans la tournée avec « Bons Enfact ».`
                : 'Conseil : créez d’abord un seul bon et vérifiez-le dans Enfact.'}
            </span>
            <div className="flex gap-2">
              {crees > 0 && (
                <button onClick={() => { fermer(); onOuvrirBons(); }} className="px-4 py-2.5 rounded-lg border-2 border-sky-700 text-sky-800 font-semibold">
                  Ouvrir « Bons Enfact »
                </button>
              )}
              <button
                onClick={() => creer(aCreer.map((c) => c.numero))}
                disabled={aCreer.length === 0 || envoi !== null}
                className="px-5 py-2.5 rounded-lg bg-sky-700 text-white font-semibold disabled:opacity-50"
              >
                {envoi === 'tous' ? 'Création…' : `Créer les ${aCreer.length} bon(s)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
